import React, {createContext, useContext, useEffect, useState} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {useSelector} from 'react-redux';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {invoiceOrderTotalWithVat, isEmail, isIsraeliIdValid} from '../../../../common/utils';
import {InvoiceOrderDocument} from '../../../../common/types';
import {RootState} from '../../../../store/configureStore';
import {usePaymentContext} from '../PaymentContext';

export type PaymentBitContext = {loading: boolean};

export type PaymentBitContextProviderProps = {children: React.ReactNode};

export const defaultContext: PaymentBitContext = {loading: false};

export type PaymentFormValues = {
  customerName: string
  service: string
  email: string
  phone: string
  amount: string
  customerAddress: string
  customerIdNumber: string
};

export const defaultFormValues: PaymentFormValues = {
  customerName: '',
  service: '',
  email: '',
  phone: '',
  amount: '',
  customerAddress: '',
  customerIdNumber: '',
};

export const PaymentBitContext = createContext<PaymentBitContext>(defaultContext);
PaymentBitContext.displayName = 'PaymentBitContext';

export const usePaymentBitContext = () => useContext(PaymentBitContext);

export const PaymentBitContextProvider = ({children}: PaymentBitContextProviderProps) => {
  const M = useMessage();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const [loading, setLoading] = useState<boolean>(false);

  const {invoiceOrder, updateLink} = usePaymentContext();

  const formik = useFormik<PaymentFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof PaymentFormValues]: string }> = {};
      if (!values.customerName) errors.customerName = M.requiredField;
      if (!values.service) errors.service = M.requiredField;
      if (!values.email) errors.email = M.requiredField;
      if (values.email && values.email.length > 0 && !isEmail(values.email)) {
        errors.email = M.notValidEmail;
      }
      if (!values.phone) errors.phone = M.requiredField;
      if (!values.amount) errors.amount = M.requiredField;

      if (parseFloat(values.amount) >= 5000 && values.customerIdNumber !== '999999999') {
        if (!values.customerIdNumber) {
          errors.customerIdNumber = M.requiredField;
        }

        if (!isIsraeliIdValid(values.customerIdNumber)) {
          errors.customerIdNumber = M.invalidIdNumberError;
        }
      }

      return errors;
    },
    onSubmit: async (values, {resetForm}) => {
      setLoading(true);
      try {
        const result = await apiService.getPaymentBitPage(
          parseFloat(values.amount),
          values.service,
          values.customerName,
          values.phone,
          values.email,
          currentLocale,
          values.customerAddress,
          values.customerIdNumber
        );
        if (result.errorCode < 0) {
          globalMessage.open(E.serverError);
        } else {
          updateLink(result.body.url);
          resetForm();
        }
      } catch (e) {
        globalMessage.open(E.serverError);
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const update = async (invoiceOrderDTO: InvoiceOrderDocument) => {
    const invoiceOrder = {
      ...invoiceOrderDTO,
      service: invoiceOrderDTO.lines.map(line => line.description).join(', '),
      lines: invoiceOrderDTO.lines.map(line => ({
        ...line,
        amount: line.amount.toString(),
      }))
    };

    await formik.setValues({
      ...defaultFormValues,
      customerName: invoiceOrder.customerName,
      service: invoiceOrder.service,
      email: invoiceOrder.emailRecipients,
      phone: invoiceOrder.phone,
      amount: invoiceOrderTotalWithVat(invoiceOrder, new Date(invoiceOrder.createdAt)).toFixed(2),
      customerAddress: invoiceOrder.address,
      customerIdNumber: invoiceOrder?.idNumber ? `${invoiceOrder?.idNumber}` : '',
    });
  };

  useEffect(() => {
    if (invoiceOrder) {
      update(invoiceOrder).catch(console.error);
    }
  }, [invoiceOrder]);

  const context: PaymentBitContext = {
    ...defaultContext,
    loading
  };

  return (
    <PaymentBitContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </PaymentBitContext.Provider>
  );
};
